/**
 * Fee Schedule Panel
 * Displays SOW-based fee line items with status tracking, manual entry,
 * and indicators for AI-extracted vs manually entered fees.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  Clock,
  AlertCircle,
  Brain,
  Loader2,
  Save,
  X,
} from 'lucide-react';
import {
  feeScheduleAPI,
  FeeScheduleItem,
  FeeType,
  FeeStatus,
} from '@/services/api/salesPipelineAPI';

interface FeeSchedulePanelProps {
  opportunityId: string;
}

const FEE_TYPE_LABELS: Record<FeeType, string> = {
  setup: 'Setup',
  recurring: 'Recurring',
  milestone: 'Milestone',
  usage: 'Usage',
  license: 'License',
  support: 'Support',
  training: 'Training',
  other: 'Other',
};

const FEE_STATUS_CONFIG: Record<FeeStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
  invoiced: { label: 'Invoiced', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-600' },
  waived: { label: 'Waived', color: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-400' },
};

const FREQUENCY_LABELS: Record<string, string> = {
  one_time: 'One-Time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
};

export const FeeSchedulePanel: React.FC<FeeSchedulePanelProps> = ({ opportunityId }) => {
  const [fees, setFees] = useState<FeeScheduleItem[]>([]);
  const [totalValue, setTotalValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFee, setEditingFee] = useState<string | null>(null);

  // Add form state
  const [newFee, setNewFee] = useState({
    feeDescription: '',
    feeType: 'recurring' as FeeType,
    amount: '',
    billingFrequency: 'monthly',
    paymentTerms: 'net_30',
    notes: '',
  });

  const loadFees = useCallback(async () => {
    setLoading(true);
    try {
      const result = await feeScheduleAPI.listFees(opportunityId);
      setFees(result.items);
      setTotalValue(result.totalValue);
    } catch (err) {
      console.error('Failed to load fees:', err);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    loadFees();
  }, [loadFees]);

  const handleAddFee = async () => {
    if (!newFee.feeDescription || !newFee.amount) return;
    try {
      await feeScheduleAPI.createFee(opportunityId, {
        feeDescription: newFee.feeDescription,
        feeType: newFee.feeType,
        amount: parseFloat(newFee.amount),
        billingFrequency: newFee.billingFrequency,
        paymentTerms: newFee.paymentTerms,
        notes: newFee.notes || undefined,
      });
      setShowAddForm(false);
      setNewFee({ feeDescription: '', feeType: 'recurring', amount: '', billingFrequency: 'monthly', paymentTerms: 'net_30', notes: '' });
      await loadFees();
    } catch (err) {
      console.error('Failed to create fee:', err);
    }
  };

  const handleUpdateStatus = async (feeId: string, status: FeeStatus) => {
    try {
      await feeScheduleAPI.updateFee(opportunityId, feeId, {
        status,
        ...(status === 'paid' ? { paidDate: new Date().toISOString().split('T')[0] } : {}),
        ...(status === 'invoiced' ? { invoicedDate: new Date().toISOString().split('T')[0] } : {}),
      });
      await loadFees();
    } catch (err) {
      console.error('Failed to update fee:', err);
    }
  };

  const handleDelete = async (feeId: string) => {
    if (!confirm('Delete this fee item?')) return;
    try {
      await feeScheduleAPI.deleteFee(opportunityId, feeId);
      await loadFees();
    } catch (err) {
      console.error('Failed to delete fee:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
        <span className="text-sm text-gray-500">Loading fee schedule...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Total */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Deal Estimate</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {fees.length} items &bull; Estimated total: ${totalValue.toLocaleString()}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5 italic">
            Estimates only. The plan tier assigned at close-won is what RevOps bills against.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <Plus className="w-4 h-4" />
          Add Fee
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <input
                type="text"
                value={newFee.feeDescription}
                onChange={(e) => setNewFee({ ...newFee, feeDescription: e.target.value })}
                placeholder="Fee description"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount ($)</label>
              <input
                type="number"
                value={newFee.amount}
                onChange={(e) => setNewFee({ ...newFee, amount: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select
                value={newFee.feeType}
                onChange={(e) => setNewFee({ ...newFee, feeType: e.target.value as FeeType })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                {Object.entries(FEE_TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Frequency</label>
              <select
                value={newFee.billingFrequency}
                onChange={(e) => setNewFee({ ...newFee, billingFrequency: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                {Object.entries(FREQUENCY_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Payment Terms</label>
              <select
                value={newFee.paymentTerms}
                onChange={(e) => setNewFee({ ...newFee, paymentTerms: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="due_on_receipt">Due on Receipt</option>
                <option value="net_15">Net 15</option>
                <option value="net_30">Net 30</option>
                <option value="net_45">Net 45</option>
                <option value="net_60">Net 60</option>
                <option value="net_90">Net 90</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAddForm(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
            <button
              onClick={handleAddFee}
              disabled={!newFee.feeDescription || !newFee.amount}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Add Fee Item
            </button>
          </div>
        </div>
      )}

      {/* Fee Items */}
      {fees.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No fee schedule items</p>
          <p className="text-xs mt-1">Upload a SOW document for AI extraction or add manually</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Type</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Frequency</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Status</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {fees.map((fee) => {
                const statusConfig = FEE_STATUS_CONFIG[fee.status];
                return (
                  <tr key={fee.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {fee.autoExtracted && (
                          <Brain className="w-3 h-3 text-purple-500 flex-shrink-0" title="AI Extracted" />
                        )}
                        <span className="text-gray-900">{fee.feeDescription}</span>
                      </div>
                      {fee.documentName && (
                        <span className="text-xs text-gray-400">from {fee.documentName}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{FEE_TYPE_LABELS[fee.feeType] || fee.feeType}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                      ${fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {FREQUENCY_LABELS[fee.billingFrequency || ''] || fee.billingFrequency || '-'}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={fee.status}
                        onChange={(e) => handleUpdateStatus(fee.id, e.target.value as FeeStatus)}
                        className={`px-1.5 py-0.5 text-xs font-medium rounded border-0 cursor-pointer ${statusConfig.color}`}
                      >
                        {Object.entries(FEE_STATUS_CONFIG).map(([val, cfg]) => (
                          <option key={val} value={val}>{cfg.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => handleDelete(fee.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t">
              <tr>
                <td colSpan={2} className="px-3 py-2 text-xs font-semibold text-gray-700">Total</td>
                <td className="px-3 py-2 text-right font-bold text-gray-900">
                  ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
